import { useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { getArticleById, articles, hotArticles } from '@/mocks/articles';
import ArticleCard from '@/components/feature/ArticleCard';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();

  const article = useMemo(() => {
    if (!id) return undefined;
    return getArticleById(id);
  }, [id]);

  const relatedArticles = useMemo(() => {
    if (!article) return [];
    return articles
      .filter((a) => a.categorySlug === article.categorySlug && a.id !== article.id)
      .slice(0, 3);
  }, [article]);

  const moreHot = useMemo(() => {
    return hotArticles.filter((a) => a.id !== id).slice(0, 4);
  }, [id]);

  if (!article) {
    return (
      <div className="max-w-[1280px] mx-auto px-4 md:px-6 py-20 text-center">
        <div className="w-20 h-20 mx-auto mb-4 flex items-center justify-center rounded-full bg-secondary-100">
          <i className="ri-emotion-sad-line text-3xl text-foreground-400"></i>
        </div>
        <h1 className="text-2xl font-heading font-bold text-foreground-950 mb-2">文章不存在</h1>
        <p className="text-foreground-500 mb-6">抱歉，该文章可能已被删除或链接错误。</p>
        <Link
          to="/"
          className="inline-flex items-center gap-2 px-6 py-2.5 rounded-full bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap"
        >
          <i className="ri-arrow-left-line"></i> 返回首页
        </Link>
      </div>
    );
  }

  return (
    <>
      <title>{`${article.title} - 吃瓜爆料`}</title>
      <meta name="description" content={article.excerpt} />
      <meta name="keywords" content={article.tags.join(',')} />
      <meta property="og:title" content={`${article.title} - 吃瓜爆料`} />
      <meta property="og:description" content={article.excerpt} />
      <meta property="og:type" content="article" />
      <meta property="og:url" content={`https://chigua.vercel.app/article/${article.id}`} />
      <meta property="og:image" content={article.imageUrl} />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={`${article.title} - 吃瓜爆料`} />
      <meta name="twitter:description" content={article.excerpt} />
      <meta name="twitter:image" content={article.imageUrl} />
      <link rel="canonical" href={`https://chigua.vercel.app/article/${article.id}`} />

      {/* Breadcrumb */}
      <div className="bg-background-100 border-b border-secondary-200/60">
        <div className="max-w-[1280px] mx-auto px-4 md:px-6 py-3">
          <nav className="flex items-center gap-2 text-sm flex-wrap" aria-label="面包屑导航">
            <Link to="/" className="text-foreground-500 hover:text-primary-500 transition-colors whitespace-nowrap">
              首页
            </Link>
            <i className="ri-arrow-right-s-line text-foreground-400 text-xs"></i>
            <Link to={`/category/${article.categorySlug}`} className="text-foreground-500 hover:text-primary-500 transition-colors whitespace-nowrap">
              {article.category}
            </Link>
            <i className="ri-arrow-right-s-line text-foreground-400 text-xs"></i>
            <span className="text-foreground-900 font-medium line-clamp-1">{article.title}</span>
          </nav>
        </div>
      </div>

      {/* Article content */}
      <div className="max-w-[1280px] mx-auto px-4 md:px-6 py-10">
        <div className="flex flex-col lg:flex-row gap-10">
          {/* Main content */}
          <article className="flex-1 min-w-0">
            {/* Featured image */}
            <div className="w-full h-[300px] md:h-[420px] rounded-lg overflow-hidden mb-8">
              <img
                src={article.imageUrl}
                alt={article.title}
                className="w-full h-full object-cover"
              />
            </div>

            {/* Meta */}
            <div className="flex items-center gap-3 mb-6 flex-wrap">
              <Link
                to={`/category/${article.categorySlug}`}
                className="px-3 py-1 rounded-full bg-primary-100 text-primary-700 text-sm font-medium hover:bg-primary-200 transition-colors whitespace-nowrap"
              >
                {article.category}
              </Link>
              {article.isHot && (
                <span className="px-3 py-1 rounded-full bg-accent-100 text-accent-700 text-sm font-medium whitespace-nowrap">
                  热门
                </span>
              )}
              <span className="text-foreground-400 text-sm">{article.publishedAt}</span>
              <span className="text-foreground-400 text-sm">
                <i className="ri-eye-line mr-1"></i>{(article.views / 10000).toFixed(1)}万 阅读
              </span>
              <span className="text-foreground-400 text-sm">
                <i className="ri-user-line mr-1"></i>{article.author}
              </span>
            </div>

            {/* Title */}
            <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-6 leading-snug">
              {article.title}
            </h1>

            {/* Content */}
            <div
              className="prose-custom text-foreground-800 leading-relaxed text-base"
              dangerouslySetInnerHTML={{ __html: article.content }}
            />

            {/* Tags */}
            <div className="mt-10 pt-6 border-t border-secondary-200/60">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-sm text-foreground-500">标签：</span>
                {article.tags.map((tag) => (
                  <span
                    key={tag}
                    className="px-3 py-1 rounded-full bg-secondary-100 text-foreground-600 text-xs hover:bg-secondary-200 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    #{tag}
                  </span>
                ))}
              </div>
            </div>

            {/* Related articles */}
            {relatedArticles.length > 0 && (
              <div className="mt-10">
                <h3 className="text-lg font-heading font-bold text-foreground-950 mb-4">相关爆料</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {relatedArticles.map((ra) => (
                    <ArticleCard key={ra.id} article={ra} />
                  ))}
                </div>
              </div>
            )}
          </article>

          {/* Sidebar */}
          <aside className="w-full lg:w-[320px] shrink-0">
            <div className="bg-background-100 rounded-lg p-5 sticky top-24">
              <div className="flex items-center gap-2 mb-4">
                <i className="ri-fire-line text-accent-500 text-lg"></i>
                <h3 className="text-lg font-heading font-bold text-foreground-950">24小时热榜</h3>
              </div>
              <div className="flex flex-col">
                {moreHot.map((ha, idx) => (
                  <ArticleCard key={ha.id} article={ha} variant="compact" />
                ))}
              </div>

              <div className="mt-6 pt-5 border-t border-secondary-200/60">
                <Link
                  to="/"
                  className="flex items-center justify-center gap-2 w-full py-2.5 rounded-full bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-arrow-left-line"></i> 返回首页
                </Link>
              </div>
            </div>
          </aside>
        </div>
      </div>

      {/* Schema.org */}
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Article",
          "headline": article.title,
          "description": article.excerpt,
          "image": article.imageUrl,
          "author": {
            "@type": "Person",
            "name": article.author
          },
          "datePublished": article.publishedAt,
          "publisher": {
            "@type": "Organization",
            "name": "吃瓜爆料",
            "url": "https://chigua.vercel.app"
          },
          "url": `https://chigua.vercel.app/article/${article.id}`,
          "mainEntityOfPage": {
            "@type": "WebPage",
            "@id": `https://chigua.vercel.app/article/${article.id}`
          }
        })}
      </script>

      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          "itemListElement": [
            {
              "@type": "ListItem",
              "position": 1,
              "name": "首页",
              "item": "https://chigua.vercel.app/"
            },
            {
              "@type": "ListItem",
              "position": 2,
              "name": article.category,
              "item": `https://chigua.vercel.app/category/${article.categorySlug}`
            },
            {
              "@type": "ListItem",
              "position": 3,
              "name": article.title
            }
          ]
        })}
      </script>
    </>
  );
}